import React from 'react';
import { CalendarClock, ImageOff, Pencil, Phone, StickyNote, User } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/Dialog';
import { useStudents } from '../../contexts/StudentsContext';
import { orDash, relativeTime } from '../../utils/format';
function DetailRow({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string;
  icon?: typeof Phone;
}) {
  return <div className="rounded-2xl border border-[var(--glass-hairline)] bg-white/50 p-3 dark:bg-white/5" data-id="element-238" data-mp="y7sw56f75kl1">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground" data-id="element-239" data-mp="1bzg2jz2us9hj">
        {Icon && <Icon className="h-3 w-3" data-id="element-240" data-mp="176d69j6oylxn" />}
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium text-foreground" data-id="element-241" data-mp="jhbsdgf3aikz">{orDash(value)}</p>
    </div>;
}
export function StudentDetailModal() {
  const {
    detail,
    closeDetail,
    openEditForm
  } = useStudents();
  return <Dialog open={Boolean(detail)} onOpenChange={open => open ? undefined : closeDetail()} data-id="element-242" data-mp="fhjxsgzdl24a">
      <DialogContent className="glass-strong max-h-[92vh] max-w-xl overflow-y-auto scrollbar-slim rounded-3xl" data-id="element-243" data-mp="kfv4iiudiwia">
        {detail && <>
            <DialogHeader data-id="element-244" data-mp="u6punyaqqg90">
              <DialogTitle className="text-lg" data-id="element-245" data-mp="u794ssu5g0ch">Student record</DialogTitle>
              <DialogDescription data-id="element-246" data-mp="3sov4qpy3svs2">Live document from the Firestore students collection.</DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center gap-4 py-2 sm:flex-row sm:items-start" data-id="element-247" data-mp="1jda1to54iic">
              <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-[var(--glass-border)] bg-muted" data-id="element-248" data-mp="6gbxjtdatr7k">
                {detail.photoURL ? <img src={detail.photoURL} alt={detail.fullNames || 'Student photo'} className="h-full w-full object-cover" data-id="element-249" data-mp="1hwtxi3g3ubyy" /> : <span className="grid h-full w-full place-items-center text-muted-foreground" data-id="element-250" data-mp="3ixqew7kau4aj">
                    <ImageOff className="h-6 w-6" data-id="element-251" data-mp="3e0hxi96z64ac" />
                  </span>}
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left" data-id="element-252" data-mp="3ry6gtcle93da">
                <h2 className="truncate text-xl font-semibold tracking-tight text-foreground" data-id="element-253" data-mp="3mzv8j6aqz0x6">
                  {orDash(detail.fullNames)}
                </h2>
                <div className="mt-2 flex flex-wrap justify-center gap-2 sm:justify-start" data-id="element-254" data-mp="2z8po0dtv3kgj">
                  {detail.className.trim() && <Badge variant="secondary" className="rounded-full" data-id="element-255" data-mp="390vgrprxu5ws">
                      {detail.className}
                    </Badge>}
                  {detail.gender && <Badge variant="outline" className="rounded-full" data-id="element-256" data-mp="2gjj2yk5ligrk">
                      {detail.gender}
                    </Badge>}
                </div>
                <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground sm:justify-start" data-id="element-257" data-mp="r3fiabtqskvx">
                  <CalendarClock className="h-3.5 w-3.5" data-id="element-258" data-mp="vu5plulqaxt9" />
                  Added {relativeTime(detail.createdAtMs)}
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2" data-id="element-259" data-mp="n1c2jb01iu4u">
              <DetailRow label="Father's name" value={detail.fatherName} icon={User} data-id="element-260" data-mp="n1txm4ayv2b6" />
              <DetailRow label="Father's telephone" value={detail.fatherPhone} icon={Phone} data-id="element-261" data-mp="1aro8igbq6gss" />
              <DetailRow label="Mother's name" value={detail.motherName} icon={User} data-id="element-262" data-mp="1ergmj8jw1f5j" />
              <DetailRow label="Mother's telephone" value={detail.motherPhone} icon={Phone} data-id="element-263" data-mp="10rxe0en601sp" />
              <div className="sm:col-span-2" data-id="element-264" data-mp="15ra7vjcge94s">
                <DetailRow label="Small note" value={detail.note} icon={StickyNote} data-id="element-265" data-mp="15u68jfforbwx" />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end" data-id="element-266" data-mp="3qi10i8lk4viw">
              <Button variant="ghost" className="h-10 rounded-xl" onClick={closeDetail} data-id="element-267" data-mp="9mjmiiybn8j7">
                Close
              </Button>
              <Button className="h-10 rounded-xl brand-gradient px-5 text-white hover:opacity-95" onClick={() => {
            const student = detail;
            closeDetail();
            openEditForm(student);
          }} data-id="element-268" data-mp="4vl2b8a7pqao">
                <Pencil className="mr-2 h-4 w-4" data-id="element-269" data-mp="slp9fpmm086f" />
                Edit record
              </Button>
            </div>
          </>}
      </DialogContent>
    </Dialog>;
}